import { useState, useEffect, useCallback, useRef } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid,
} from 'recharts';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';
import toast from 'react-hot-toast';
import './AdminPanel.css';

// ─── Constants ────────────────────────────────────────────────────────────────
const TABS = ['Overview', 'Users', 'Challenges', 'Payouts', 'Crypto Payments', 'Transactions', 'Plans'];

const STATUS_COLORS = {
  active:  'var(--green)',
  passed:  'var(--accent)',
  failed:  'var(--red)',
  pending: 'var(--yellow)',
};

// ─── Formatting helpers ───────────────────────────────────────────────────────
const fmt    = n  => (typeof n === 'number' ? n.toLocaleString() : n ?? '—');
const fmtUSD = n  => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmtDateTime = d => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const initials = name => name ? name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : '?';

// ─── Shared: Stat Tile ────────────────────────────────────────────────────────
function Tile({ label, value, color = 'var(--accent)', prefix = '', sub }) {
  return (
    <div className="admin-tile card">
      <span className="tile-label">{label}</span>
      <span className="tile-value mono" style={{ color }}>
        {prefix}{typeof value === 'number' ? value.toLocaleString() : value ?? '—'}
      </span>
      {sub && <span className="tile-sub">{sub}</span>}
    </div>
  );
}

// ─── Shared: Custom Recharts Tooltip ─────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip-label">{label}</p>
      {payload.map(p => (
        <p key={p.dataKey} style={{ color: p.color, fontFamily: 'var(--font-mono)', fontSize: 12 }}>
          {p.name}: {p.dataKey === 'revenue' ? fmtUSD(p.value) : fmt(p.value)}
        </p>
      ))}
    </div>
  );
}

// ─── Shared: Empty state ──────────────────────────────────────────────────────
function EmptyState({ message = 'No data found', icon = '📭' }) {
  return (
    <div className="empty-state">
      <span className="empty-icon">{icon}</span>
      <p>{message}</p>
    </div>
  );
}

// ─── Shared: Pagination ───────────────────────────────────────────────────────
function Pagination({ page, totalPages, onPrev, onNext, total }) {
  if (totalPages <= 1) return null;
  return (
    <div className="pagination">
      <button className="btn btn-sm btn-outline" disabled={page <= 1} onClick={onPrev}>← Prev</button>
      <span className="pagination-info">
        Page {page} of {totalPages}
        {total != null && <span className="pagination-total"> · {total.toLocaleString()} total</span>}
      </span>
      <button className="btn btn-sm btn-outline" disabled={page >= totalPages} onClick={onNext}>Next →</button>
    </div>
  );
}

// ─── User Detail Modal ────────────────────────────────────────────────────────
function UserModal({ userId, onClose, onUserUpdate }) {
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing]   = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/users/${userId}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load user details'))
      .finally(() => setLoading(false));
  }, [userId]);

  // Close on Escape
  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleAction = async (action, payload) => {
    setActing(action);
    try {
      let res;
      if (action === 'verify') {
        res = await api.put(`/admin/users/${userId}/verify`, payload);
      } else if (action === 'status') {
        res = await api.put(`/admin/users/${userId}/deactivate`, { active: payload.active });
      } else if (action === 'role') {
        res = await api.put(`/admin/users/${userId}/role`, payload);
      }
      const updatedUser = res.data.user;
      setData(prev => ({ ...prev, user: updatedUser }));
      onUserUpdate?.(updatedUser);
      toast.success(res.data.message || 'Updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Action failed');
    } finally {
      setActing(null);
    }
  };

  const user         = data?.user;
  const challenges   = data?.challenges || [];
  const transactions = data?.transactions || [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="user-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose} aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        {loading ? (
          <div className="modal-loading">
            <div className="modal-spinner" />
            <p>Loading user details…</p>
          </div>
        ) : !user ? (
          <EmptyState message="User not found" icon="🔍" />
        ) : (
          <div className="modal-body">

            {/* ── Header ── */}
            <div className="modal-user-header">
              <div className="modal-avatar">
                {user.avatar
                  ? <img src={user.avatar} alt={user.name} className="modal-avatar-img" />
                  : <span>{initials(user.name)}</span>
                }
              </div>
              <div className="modal-user-meta">
                <h2 className="modal-user-name">{user.name}</h2>
                <p className="modal-user-email">{user.email}</p>
                <div className="modal-badges">
                  <span className={`badge ${user.role === 'admin' ? 'badge-passed' : 'badge-pending'}`}>{user.role}</span>
                  <span className={`badge ${user.isActive ? 'badge-active' : 'badge-failed'}`}>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </span>
                  <span className={`badge ${user.isVerified ? 'badge-active' : 'badge-pending'}`}>
                    {user.isVerified ? '✓ Approved' : 'Unverified'}
                  </span>
                  {user.authProvider === 'google' && (
                    <span className="badge badge-google">Google</span>
                  )}
                </div>
              </div>
            </div>

            {/* ── Action buttons ── */}
            <div className="modal-actions">
              <button
                className={`btn btn-sm ${user.isVerified ? 'btn-danger' : 'btn-success'}`}
                disabled={acting === 'verify'}
                onClick={() => {
                  const msg = user.isVerified
                    ? `Revoke approval for ${user.name}?`
                    : `Approve ${user.name}?`;
                  if (window.confirm(msg)) handleAction('verify', { verified: !user.isVerified });
                }}
              >
                {acting === 'verify' ? '…' : user.isVerified ? 'Revoke Approval' : '✓ Approve User'}
              </button>

              <button
                className={`btn btn-sm ${user.isActive ? 'btn-danger' : 'btn-success'}`}
                disabled={acting === 'status'}
                onClick={() => {
                  const msg = user.isActive
                    ? `Deactivate ${user.name}'s account?`
                    : `Reactivate ${user.name}'s account?`;
                  if (window.confirm(msg)) handleAction('status', { active: !user.isActive });
                }}
              >
                {acting === 'status' ? '…' : user.isActive ? 'Deactivate' : 'Activate Account'}
              </button>

              {user.role !== 'admin' ? (
                <button
                  className="btn btn-sm btn-outline"
                  disabled={acting === 'role'}
                  onClick={() => {
                    if (window.confirm(`Promote ${user.name} to Admin?`))
                      handleAction('role', { role: 'admin' });
                  }}
                >
                  {acting === 'role' ? '…' : '⬆ Promote to Admin'}
                </button>
              ) : (
                <button
                  className="btn btn-sm btn-danger"
                  disabled={acting === 'role'}
                  onClick={() => {
                    if (window.confirm(`Revoke admin role from ${user.name}?`))
                      handleAction('role', { role: 'user' });
                  }}
                >
                  {acting === 'role' ? '…' : 'Revoke Admin'}
                </button>
              )}
            </div>

            {/* ── Profile details grid ── */}
            <div className="modal-section">
              <h4 className="modal-section-title">Profile Details</h4>
              <div className="modal-detail-grid">
                {[
                  ['First Name',        user.firstName || '—'],
                  ['Last Name',         user.lastName  || '—'],
                  ['Phone',             user.phone     || '—'],
                  ['Country',           user.country   || '—'],
                  ['Auth Provider',     user.authProvider || 'local'],
                  ['Profile Complete',  user.profileCompleted ? 'Yes' : 'No'],
                  ['Joined',            fmtDate(user.createdAt)],
                  ['Last Login',        fmtDateTime(user.lastLogin)],
                ].map(([k, v]) => (
                  <div key={k} className="modal-detail-item">
                    <span className="modal-detail-label">{k}</span>
                    <span className="modal-detail-value">{v}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Stats ── */}
            <div className="modal-section">
              <h4 className="modal-section-title">Account Stats</h4>
              <div className="modal-stats-grid">
                <div className="modal-stat">
                  <span className="modal-stat-value">{fmt(user.stats?.totalChallenges)}</span>
                  <label className="modal-stat-label">Challenges</label>
                </div>
                <div className="modal-stat">
                  <span className="modal-stat-value" style={{ color: 'var(--green)' }}>{fmt(user.stats?.passedChallenges)}</span>
                  <label className="modal-stat-label">Passed</label>
                </div>
                <div className="modal-stat">
                  <span className="modal-stat-value" style={{ color: 'var(--red)' }}>{fmt(user.stats?.failedChallenges)}</span>
                  <label className="modal-stat-label">Failed</label>
                </div>
                <div className="modal-stat">
                  <span className="modal-stat-value" style={{ color: 'var(--gold)' }}>{fmtUSD(user.stats?.totalProfitSim)}</span>
                  <label className="modal-stat-label">Sim Profit</label>
                </div>
              </div>
            </div>

            {/* ── Recent Challenges ── */}
            {challenges.length > 0 && (
              <div className="modal-section">
                <h4 className="modal-section-title">Recent Challenges ({challenges.length})</h4>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr><th>Plan</th><th>Balance</th><th>P&L</th><th>Status</th><th>Started</th></tr>
                    </thead>
                    <tbody>
                      {challenges.map(c => {
                        const pnl = (c.currentBalance || 0) - (c.startingBalance || 0);
                        return (
                          <tr key={c._id}>
                            <td>{c.plan?.name || '—'}</td>
                            <td className="mono">{fmtUSD(c.currentBalance)}</td>
                            <td className="mono" style={{ color: pnl >= 0 ? 'var(--green)' : 'var(--red)', fontWeight: 700 }}>
                              {pnl >= 0 ? '+' : ''}{fmtUSD(pnl)}
                            </td>
                            <td><span className={`badge badge-${c.status}`}>● {c.status}</span></td>
                            <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmtDate(c.startedAt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── Recent Transactions ── */}
            {transactions.length > 0 && (
              <div className="modal-section">
                <h4 className="modal-section-title">Recent Transactions ({transactions.length})</h4>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr><th>Plan</th><th>Amount</th><th>Type</th><th>Status</th><th>Date</th></tr>
                    </thead>
                    <tbody>
                      {transactions.map(t => (
                        <tr key={t._id}>
                          <td>{t.plan?.name || '—'}</td>
                          <td className="mono text-gold">{fmtUSD(t.amount)}</td>
                          <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t.type?.replace(/_/g, ' ')}</td>
                          <td>
                            <span className={`badge badge-${t.status === 'completed' ? 'passed' : t.status === 'failed' ? 'failed' : 'pending'}`}>
                              {t.status}
                            </span>
                          </td>
                          <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmtDate(t.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {challenges.length === 0 && transactions.length === 0 && (
              <EmptyState message="No challenges or transactions yet" icon="📊" />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab: Overview ────────────────────────────────────────────────────────────
function OverviewTab({ stats }) {
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    api.get('/admin/analytics')
      .then(r => setAnalytics(r.data.chartData))
      .catch(() => {}); // non-critical, silently skip
  }, []);

  if (!stats) return <p className="text-muted">Loading stats…</p>;

  const pieData = [
    { name: 'Active', value: stats.activeChallenges  },
    { name: 'Passed', value: stats.passedChallenges  },
    { name: 'Failed', value: stats.failedChallenges  },
  ].filter(d => d.value > 0);

  const barData = [
    { name: 'Users',      count: stats.totalUsers      },
    { name: 'Challenges', count: stats.totalChallenges },
    { name: 'Trades',     count: stats.totalTrades     },
  ];

  return (
    <div>
      {/* Stat tiles */}
      <div className="tiles-grid">
        <Tile label="Total Users"       value={stats.totalUsers} />
        <Tile label="Total Challenges"  value={stats.totalChallenges} />
        <Tile label="Active Now"        value={stats.activeChallenges}  color="var(--green)" />
        <Tile label="Passed"            value={stats.passedChallenges}  color="var(--accent)" />
        <Tile label="Failed"            value={stats.failedChallenges}  color="var(--red)" />
        <Tile label="Total Trades"      value={stats.totalTrades}       color="var(--text-secondary)" />
        <Tile label="Platform Revenue"  value={stats.totalRevenue} prefix="$" color="var(--gold)" />
        <Tile label="Pending Payouts"   value={stats.pendingPayouts}    color="var(--yellow)" />
      </div>

      {/* Challenge status + platform totals */}
      <div className="admin-charts">
        <div className="card">
          <h3 className="admin-card-title">Challenge Distribution</h3>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label>
                  {pieData.map(entry => (
                    <Cell key={entry.name} fill={STATUS_COLORS[entry.name.toLowerCase()] || 'var(--accent)'} />
                  ))}
                </Pie>
                <Legend formatter={v => <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{v}</span>} />
                <Tooltip contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState message="No challenge data yet" icon="📈" />
          )}
        </div>

        <div className="card">
          <h3 className="admin-card-title">Platform Totals</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={barData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="count" fill="var(--accent)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Time-series analytics (when available) */}
      {analytics && analytics.length > 0 && (
        <>
          <div className="analytics-heading">
            <h3 className="admin-card-title" style={{ marginBottom: 0 }}>Monthly Analytics — Last 6 Months</h3>
          </div>
          <div className="admin-charts" style={{ marginTop: 0 }}>
            <div className="card">
              <h3 className="admin-card-title">Revenue</h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={analytics} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="month" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
                  <Tooltip content={<ChartTooltip />} />
                  <Line
                    type="monotone" dataKey="revenue" name="Revenue"
                    stroke="var(--gold)" strokeWidth={2.5}
                    dot={{ fill: 'var(--gold)', r: 4, strokeWidth: 0 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="card">
              <h3 className="admin-card-title">New Users</h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={analytics} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="month" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Line
                    type="monotone" dataKey="users" name="Users"
                    stroke="var(--accent)" strokeWidth={2.5}
                    dot={{ fill: 'var(--accent)', r: 4, strokeWidth: 0 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Tab: Users ───────────────────────────────────────────────────────────────
function UsersTab() {
  const [users, setUsers]           = useState([]);
  const [search, setSearch]         = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]           = useState(0);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const debounceRef = useRef(null);

  const load = useCallback((q, sf, p) => {
    setLoading(true);
    let url = `/admin/users?page=${p}&limit=20`;
    if (q)               url += `&search=${encodeURIComponent(q)}`;
    if (sf && sf !== 'all') url += `&status=${sf}`;
    api.get(url)
      .then(r => {
        setUsers(r.data.users || []);
        setTotalPages(r.data.pages || 1);
        setTotal(r.data.total || 0);
      })
      .catch(() => toast.error('Failed to load users'))
      .finally(() => setLoading(false));
  }, []);

  // Debounce search / filter changes
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      load(search, statusFilter, 1);
    }, 380);
    return () => clearTimeout(debounceRef.current);
  }, [search, statusFilter, load]);

  // Page-only changes (no debounce needed)
  const goToPage = useCallback(p => {
    setPage(p);
    load(search, statusFilter, p);
  }, [search, statusFilter, load]);

  const handleUserUpdate = useCallback(updatedUser => {
    setUsers(prev => prev.map(u => u._id === updatedUser._id ? { ...u, ...updatedUser } : u));
  }, []);

  const STATUS_PILLS = [
    ['all', 'All'],
    ['active', 'Active'],
    ['inactive', 'Inactive'],
    ['verified', 'Approved'],
    ['unverified', 'Pending'],
  ];

  return (
    <div>
      {/* Controls */}
      <div className="table-controls">
        <div className="search-wrap">
          <svg className="search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            className="input-field search-input"
            placeholder="Search by name or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div className="filter-pills">
          {STATUS_PILLS.map(([val, label]) => (
            <button
              key={val}
              className={`btn btn-sm ${statusFilter === val ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setStatusFilter(val)}
            >
              {label}
            </button>
          ))}
        </div>

        <span className="record-count">{total.toLocaleString()} users</span>
      </div>

      {/* Table */}
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Auth</th>
              <th>Challenges</th>
              <th>Passed</th>
              <th>Approval</th>
              <th>Joined</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="table-loading">
                  <div className="table-spinner" /> Loading users…
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={8} className="table-loading">No users match your search.</td>
              </tr>
            ) : users.map(u => (
              <tr
                key={u._id}
                className="clickable-row"
                onClick={() => setSelectedUserId(u._id)}
                title="Click to view details"
              >
                <td>
                  <div className="user-cell">
                    <div className="user-avatar-sm">
                      {u.avatar
                        ? <img src={u.avatar} alt={u.name} />
                        : initials(u.name)
                      }
                    </div>
                    <div>
                      <div className="user-cell-name">{u.name}</div>
                      <div className="user-cell-email">{u.email}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <span className={`badge ${u.role === 'admin' ? 'badge-passed' : 'badge-pending'}`}>
                    {u.role}
                  </span>
                </td>
                <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {u.authProvider === 'google' ? '🔵 Google' : '🔑 Email'}
                </td>
                <td className="mono">{u.stats?.totalChallenges || 0}</td>
                <td className="mono text-green">{u.stats?.passedChallenges || 0}</td>
                <td>
                  <span className={`badge ${u.isVerified ? 'badge-active' : 'badge-pending'}`}>
                    {u.isVerified ? '✓ Approved' : 'Pending'}
                  </span>
                </td>
                <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmtDate(u.createdAt)}</td>
                <td>
                  <span className={`status-indicator ${u.isActive ? 'status-active' : 'status-inactive'}`}>
                    <span className="status-dot" />
                    {u.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        onPrev={() => goToPage(page - 1)}
        onNext={() => goToPage(page + 1)}
      />

      {selectedUserId && (
        <UserModal
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
          onUserUpdate={handleUserUpdate}
        />
      )}
    </div>
  );
}

// ─── Tab: Challenges ──────────────────────────────────────────────────────────
function ChallengesTab() {
  const [challenges, setChallenges] = useState([]);
  const [filter, setFilter]         = useState('');
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]           = useState(0);

  const load = useCallback((status, p) => {
    setLoading(true);
    let url = `/challenges/admin/all?page=${p}&limit=20`;
    if (status) url += `&status=${status}`;
    api.get(url)
      .then(r => {
        setChallenges(r.data.challenges || []);
        setTotalPages(r.data.pages || 1);
        setTotal(r.data.total || 0);
      })
      .catch(() => toast.error('Failed to load challenges'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setPage(1);
    load(filter, 1);
  }, [filter, load]);

  const goToPage = useCallback(p => {
    setPage(p);
    load(filter, p);
  }, [filter, load]);

  return (
    <div>
      <div className="table-controls">
        <div className="filter-pills">
          {['', 'active', 'passed', 'failed'].map(s => (
            <button
              key={s}
              className={`btn btn-sm ${filter === s ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setFilter(s)}
            >
              {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
            </button>
          ))}
        </div>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr><th>User</th><th>Plan</th><th>Balance</th><th>P&L</th><th>Status</th><th>Bot</th><th>Started</th></tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="table-loading"><div className="table-spinner" /> Loading…</td></tr>
            ) : challenges.length === 0 ? (
              <tr><td colSpan={7} className="table-loading">No challenges found.</td></tr>
            ) : challenges.map(c => {
              const pnl = (c.currentBalance || 0) - (c.startingBalance || 0);
              return (
                <tr key={c._id}>
                  <td>
                    <strong>{c.user?.name}</strong>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.user?.email}</div>
                  </td>
                  <td>{c.plan?.name}</td>
                  <td className="mono">{fmtUSD(c.currentBalance)}</td>
                  <td className="mono" style={{ color: pnl >= 0 ? 'var(--green)' : 'var(--red)', fontWeight: 700 }}>
                    {pnl >= 0 ? '+' : ''}{fmtUSD(pnl)}
                  </td>
                  <td><span className={`badge badge-${c.status}`}>● {c.status}</span></td>
                  <td>
                    {c.botRunning
                      ? <span style={{ color: 'var(--green)', fontSize: 12, fontFamily: 'var(--font-mono)' }}>● Active</span>
                      : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                    }
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmtDate(c.startedAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        onPrev={() => goToPage(page - 1)}
        onNext={() => goToPage(page + 1)}
      />
    </div>
  );
}

// ─── Tab: Payouts ─────────────────────────────────────────────────────────────
function PayoutsTab() {
  const [payouts, setPayouts]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState('pending');
  const [page, setPage]           = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]         = useState(0);
  const [acting, setActing]       = useState(null);

  const load = useCallback((status, p) => {
    setLoading(true);
    let url = `/admin/payouts?page=${p}&limit=20`;
    if (status && status !== 'all') url += `&status=${status}`;
    api.get(url)
      .then(r => {
        setPayouts(r.data.payouts || []);
        setTotalPages(r.data.pages || 1);
        setTotal(r.data.total || 0);
      })
      .catch(() => toast.error('Failed to load payouts'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setPage(1);
    load(filter, 1);
  }, [filter, load]);

  const goToPage = useCallback(p => {
    setPage(p);
    load(filter, p);
  }, [filter, load]);

  const handleAction = async (id, action, userName) => {
    const label = action === 'approve' ? 'approve' : 'deny';
    if (!window.confirm(`${label.charAt(0).toUpperCase() + label.slice(1)} payout for ${userName}?`)) return;
    setActing(id);
    try {
      const { data } = await api.put(`/challenges/admin/${id}/payout`, { action });
      setPayouts(prev => prev.map(p =>
        p._id === id ? { ...p, payoutStatus: data.challenge.payoutStatus } : p
      ));
      toast.success(`Payout ${data.challenge.payoutStatus}`);
    } catch {
      toast.error('Failed to update payout');
    } finally {
      setActing(null);
    }
  };

  const FILTER_PILLS = [
    ['pending', 'Pending'],
    ['approved', 'Approved'],
    ['denied', 'Denied'],
    ['all', 'All'],
  ];

  return (
    <div>
      <div className="table-controls">
        <div className="filter-pills">
          {FILTER_PILLS.map(([val, label]) => (
            <button
              key={val}
              className={`btn btn-sm ${filter === val ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setFilter(val)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr><th>User</th><th>Plan</th><th>Sim Balance</th><th>Est. Payout</th><th>Status</th><th>Requested</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="table-loading"><div className="table-spinner" /> Loading…</td></tr>
            ) : payouts.length === 0 ? (
              <tr><td colSpan={7} className="table-loading">No payout requests for this filter.</td></tr>
            ) : payouts.map(p => {
              const profit    = (p.currentBalance || 0) - (p.startingBalance || 0);
              const simPayout = Math.max(0, profit * ((p.plan?.payoutSplit || 80) / 100));
              return (
                <tr key={p._id}>
                  <td>
                    <strong>{p.user?.name}</strong>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.user?.email}</div>
                  </td>
                  <td>{p.plan?.name}</td>
                  <td className="mono">{fmtUSD(p.currentBalance)}</td>
                  <td className="mono text-green">{fmtUSD(simPayout)} <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>(sim)</span></td>
                  <td>
                    <span className={`badge badge-${p.payoutStatus === 'approved' ? 'passed' : p.payoutStatus === 'denied' ? 'failed' : 'pending'}`}>
                      {p.payoutStatus}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmtDate(p.updatedAt || p.createdAt)}</td>
                  <td>
                    {p.payoutStatus === 'pending' && (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          className="btn btn-sm btn-success"
                          disabled={acting === p._id}
                          onClick={() => handleAction(p._id, 'approve', p.user?.name)}
                        >✓ Approve</button>
                        <button
                          className="btn btn-sm btn-danger"
                          disabled={acting === p._id}
                          onClick={() => handleAction(p._id, 'deny', p.user?.name)}
                        >✕ Deny</button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        onPrev={() => goToPage(page - 1)}
        onNext={() => goToPage(page + 1)}
      />
    </div>
  );
}

// ─── Tab: Crypto Payments ─────────────────────────────────────────────────────
function CryptoPaymentsTab() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('awaiting_verification');
  const [acting, setActing]     = useState(null);
  const [notes, setNotes]       = useState({});

  const load = useCallback(status => {
    setLoading(true);
    api.get(`/admin/crypto-payments?status=${status}`)
      .then(r => setPayments(r.data.payments || []))
      .catch(() => toast.error('Failed to load crypto payments'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(filter); }, [filter, load]);

  const handleAction = async (id, action) => {
    const label = action === 'approve' ? 'approve this payment and activate a challenge' : 'reject this payment';
    if (!window.confirm(`Are you sure you want to ${label}?`)) return;
    setActing(id);
    try {
      await api.put(`/admin/crypto-payments/${id}/verify`, { action, adminNote: notes[id] || '' });
      toast.success(action === 'approve' ? '✅ Payment approved — challenge activated!' : '❌ Payment rejected');
      setPayments(prev => prev.filter(p => p._id !== id));
      setNotes(prev => { const n = { ...prev }; delete n[id]; return n; });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Action failed');
    } finally {
      setActing(null);
    }
  };

  const COIN_COLORS = { USDT_TRC20: '#26a17b', USDT_ERC20: '#627eea', BTC: '#f7931a' };
  const COIN_ICONS  = { USDT_TRC20: '₮', USDT_ERC20: '₮', BTC: '₿' };
  const FILTER_PILLS = [
    ['awaiting_verification', 'Pending'],
    ['completed', 'Approved'],
    ['failed', 'Rejected'],
    ['all', 'All'],
  ];

  return (
    <div>
      <div className="table-controls">
        <div className="filter-pills">
          {FILTER_PILLS.map(([val, label]) => (
            <button
              key={val}
              className={`btn btn-sm ${filter === val ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setFilter(val)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="table-loading"><div className="table-spinner" /> Loading…</div>
      ) : payments.length === 0 ? (
        <EmptyState message="No crypto payments for this filter." icon="₿" />
      ) : (
        <div className="crypto-list">
          {payments.map(p => (
            <div key={p._id} className="card crypto-card">
              <div className="crypto-card-main">
                <div className="crypto-card-info">
                  <div className="crypto-card-user">
                    <strong>{p.user?.name}</strong>
                    <span className="crypto-card-email">{p.user?.email}</span>
                    <span className={`badge badge-${p.status === 'completed' ? 'passed' : p.status === 'failed' ? 'failed' : 'pending'}`}>
                      {p.status}
                    </span>
                  </div>
                  <div className="crypto-card-meta">
                    <span>Plan: <strong>{p.plan?.name}</strong></span>
                    <span className="mono text-gold">${p.amount} USD</span>
                    <span style={{ color: COIN_COLORS[p.cryptoCoin] || 'var(--text-secondary)', fontWeight: 700 }}>
                      {COIN_ICONS[p.cryptoCoin]} {p.cryptoCoin?.replace('_', ' · ')}
                    </span>
                    <span>Platform: <strong>{p.tradingPlatform}</strong></span>
                    <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{fmtDateTime(p.createdAt)}</span>
                  </div>
                  {p.cryptoTxHash && (
                    <div className="crypto-txhash">TxHash: {p.cryptoTxHash}</div>
                  )}
                  {p.adminNote && (
                    <div className="crypto-admin-note">Note: {p.adminNote}</div>
                  )}
                </div>

                {p.status === 'awaiting_verification' && (
                  <div className="crypto-card-actions">
                    <input
                      className="input-field"
                      placeholder="Admin note (optional)"
                      value={notes[p._id] || ''}
                      onChange={e => setNotes(prev => ({ ...prev, [p._id]: e.target.value }))}
                      style={{ fontSize: 12, padding: '6px 10px' }}
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        className="btn btn-sm btn-success"
                        style={{ flex: 1 }}
                        onClick={() => handleAction(p._id, 'approve')}
                        disabled={acting === p._id}
                      >
                        {acting === p._id ? '…' : '✓ Approve'}
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleAction(p._id, 'reject')}
                        disabled={acting === p._id}
                      >
                        ✕ Reject
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Tab: Transactions ────────────────────────────────────────────────────────
function TransactionsTab() {
  const [txns, setTxns]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage]             = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]           = useState(0);
  const debounceRef = useRef(null);

  const load = useCallback((q, s, p) => {
    setLoading(true);
    let url = `/admin/transactions?page=${p}&limit=20`;
    if (q) url += `&search=${encodeURIComponent(q)}`;
    if (s) url += `&status=${s}`;
    api.get(url)
      .then(r => {
        setTxns(r.data.transactions || []);
        setTotalPages(r.data.pages || 1);
        setTotal(r.data.total || 0);
      })
      .catch(() => toast.error('Failed to load transactions'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      load(search, statusFilter, 1);
    }, 380);
    return () => clearTimeout(debounceRef.current);
  }, [search, statusFilter, load]);

  const goToPage = useCallback(p => {
    setPage(p);
    load(search, statusFilter, p);
  }, [search, statusFilter, load]);

  const STATUS_PILLS = [
    ['', 'All'],
    ['completed', 'Completed'],
    ['pending', 'Pending'],
    ['failed', 'Failed'],
  ];

  return (
    <div>
      <div className="table-controls">
        <div className="search-wrap">
          <svg className="search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            className="input-field search-input"
            placeholder="Search by user name or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div className="filter-pills">
          {STATUS_PILLS.map(([val, label]) => (
            <button
              key={val}
              className={`btn btn-sm ${statusFilter === val ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setStatusFilter(val)}
            >
              {label}
            </button>
          ))}
        </div>

        <span className="record-count">{total.toLocaleString()} transactions</span>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr><th>User</th><th>Plan</th><th>Amount</th><th>Type</th><th>Method</th><th>Status</th><th>Date</th></tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="table-loading"><div className="table-spinner" /> Loading…</td></tr>
            ) : txns.length === 0 ? (
              <tr><td colSpan={7} className="table-loading">No transactions match your search.</td></tr>
            ) : txns.map(t => (
              <tr key={t._id}>
                <td>
                  <strong>{t.user?.name || '—'}</strong>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t.user?.email}</div>
                </td>
                <td style={{ fontSize: 13 }}>{t.plan?.name || '—'}</td>
                <td className="mono text-gold" style={{ fontWeight: 700 }}>{fmtUSD(t.amount)}</td>
                <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t.type?.replace(/_/g, ' ')}</td>
                <td style={{ fontSize: 12 }}>{t.paymentMethod || 'stripe'}</td>
                <td>
                  <span className={`badge badge-${t.status === 'completed' ? 'passed' : t.status === 'failed' ? 'failed' : 'pending'}`}>
                    {t.status}
                  </span>
                </td>
                <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmtDateTime(t.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        onPrev={() => goToPage(page - 1)}
        onNext={() => goToPage(page + 1)}
      />
    </div>
  );
}

// ─── Tab: Plans ───────────────────────────────────────────────────────────────
function PlansTab() {
  const [plans, setPlans]       = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState({
    name: '', price: '', simBalance: '',
    'rules.profitTargetPct': '10',
    'rules.maxDailyLossPct': '5',
    'rules.maxDrawdownPct':  '10',
    'rules.timeLimitDays':   '30',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/plans').then(r => setPlans(r.data.plans || []));
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name:       form.name,
        price:      Number(form.price),
        simBalance: Number(form.simBalance),
        rules: {
          profitTargetPct: Number(form['rules.profitTargetPct']),
          maxDailyLossPct: Number(form['rules.maxDailyLossPct']),
          maxDrawdownPct:  Number(form['rules.maxDrawdownPct']),
          timeLimitDays:   Number(form['rules.timeLimitDays']),
        },
      };
      const { data } = await api.post('/plans', payload);
      setPlans(prev => [...prev, data.plan]);
      setShowForm(false);
      toast.success('Plan created successfully');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create plan');
    } finally {
      setSaving(false);
    }
  };

  const togglePlan = async (plan) => {
    try {
      const { data } = await api.put(`/plans/${plan._id}`, { isActive: !plan.isActive });
      setPlans(prev => prev.map(p => p._id === plan._id ? data.plan : p));
      toast.success(`Plan ${data.plan.isActive ? 'activated' : 'deactivated'}`);
    } catch {
      toast.error('Failed to update plan');
    }
  };

  const PLAN_FIELDS = [
    ['name',                  'Plan Name',       'text',   'Elite'],
    ['price',                 'Price ($)',        'number', '50'],
    ['simBalance',            'Sim Balance ($)',  'number', '100000'],
    ['rules.profitTargetPct', 'Profit Target %', 'number', '10'],
    ['rules.maxDailyLossPct', 'Daily Loss %',    'number', '5'],
    ['rules.maxDrawdownPct',  'Drawdown %',      'number', '10'],
    ['rules.timeLimitDays',   'Days Limit',      'number', '30'],
  ];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <button className="btn btn-primary btn-sm" onClick={() => setShowForm(v => !v)}>
          {showForm ? 'Cancel' : '+ Create Plan'}
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 24, maxWidth: 560 }}>
          <h3 className="admin-card-title">New Plan</h3>
          <form onSubmit={handleCreate} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {PLAN_FIELDS.map(([key, label, type, placeholder]) => (
              <div key={key} className="input-group">
                <label className="input-label">{label}</label>
                <input
                  className="input-field"
                  type={type}
                  placeholder={placeholder}
                  required
                  value={form[key]}
                  onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))}
                />
              </div>
            ))}
            <div style={{ gridColumn: '1/-1' }}>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Creating…' : 'Create Plan'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr><th>Name</th><th>Price</th><th>Sim Balance</th><th>Target</th><th>Daily Loss</th><th>Drawdown</th><th>Days</th><th>Status</th></tr>
          </thead>
          <tbody>
            {plans.length === 0 ? (
              <tr><td colSpan={8} className="table-loading">No plans created yet.</td></tr>
            ) : plans.map(p => (
              <tr key={p._id}>
                <td>
                  <strong>{p.name}</strong>
                  {p.badgeLabel && <span className="badge badge-passed" style={{ marginLeft: 8, fontSize: 10 }}>{p.badgeLabel}</span>}
                </td>
                <td className="mono text-gold">${p.price}</td>
                <td className="mono text-accent">${p.simBalance?.toLocaleString()}</td>
                <td className="mono text-green">{p.rules?.profitTargetPct}%</td>
                <td className="mono text-red">{p.rules?.maxDailyLossPct}%</td>
                <td className="mono" style={{ color: 'var(--yellow)' }}>{p.rules?.maxDrawdownPct}%</td>
                <td className="mono">{p.rules?.timeLimitDays}d</td>
                <td>
                  <button
                    className={`btn btn-sm ${p.isActive ? 'btn-danger' : 'btn-success'}`}
                    onClick={() => togglePlan(p)}
                  >
                    {p.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main Admin Panel ──────────────────────────────────────────────────────────
export default function AdminPanel() {
  const [activeTab, setActiveTab] = useState('Overview');
  const [stats, setStats]         = useState(null);

  useEffect(() => {
    api.get('/admin/stats')
      .then(r => setStats(r.data))
      .catch(() => {}); // silently fail if stats unavailable
  }, []);

  const TAB_ICONS = {
    'Overview':       '📊',
    'Users':          '👥',
    'Challenges':     '🏆',
    'Payouts':        '💸',
    'Crypto Payments':'₿',
    'Transactions':   '💳',
    'Plans':          '📋',
  };

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content admin-main">

        {/* Header */}
        <div className="admin-header">
          <div>
            <div className="admin-header-eyebrow">AlphaVault Management</div>
            <h1>Admin Panel</h1>
            <p className="text-secondary" style={{ marginTop: 4, fontSize: 14 }}>
              Full platform control — users, payments, challenges & analytics
            </p>
          </div>
          {stats && (
            <div className="admin-header-kpis">
              <div className="header-kpi">
                <span className="header-kpi-value">{stats.totalUsers}</span>
                <span className="header-kpi-label">Users</span>
              </div>
              <div className="header-kpi">
                <span className="header-kpi-value text-gold">${stats.totalRevenue?.toLocaleString() || 0}</span>
                <span className="header-kpi-label">Revenue</span>
              </div>
              <div className="header-kpi">
                <span className="header-kpi-value text-green">{stats.activeChallenges}</span>
                <span className="header-kpi-label">Active</span>
              </div>
            </div>
          )}
        </div>

        {/* Tab bar */}
        <div className="admin-tabs">
          {TABS.map(tab => (
            <button
              key={tab}
              className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              <span className="tab-icon">{TAB_ICONS[tab]}</span>
              {tab}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="admin-content">
          {activeTab === 'Overview'        && <OverviewTab stats={stats} />}
          {activeTab === 'Users'           && <UsersTab />}
          {activeTab === 'Challenges'      && <ChallengesTab />}
          {activeTab === 'Payouts'         && <PayoutsTab />}
          {activeTab === 'Crypto Payments' && <CryptoPaymentsTab />}
          {activeTab === 'Transactions'    && <TransactionsTab />}
          {activeTab === 'Plans'           && <PlansTab />}
        </div>
      </main>
    </div>
  );
}
